import { useState, useRef, useCallback, useEffect } from "react";
import moment from "moment";
import axios from "axios";
import * as facemesh from "@tensorflow-models/facemesh";
import "@tensorflow/tfjs";
import {
  useValidateProgrammingTest,
  useSubmitProgrammingTest,
  useBlockProgrammingTestById,
  useSendProgrammingTestOTP,
} from "../../../store/query/scheduleProgrammingTestQuery";
import { getPythonBaseUrl } from "../../../utils/axiosInstance";

const STORAGE_KEY_PREFIX = "codingAssessmentState_";
const TIMER_KEY_PREFIX = "codingAssessmentTimer_";
const TEST_DURATION = 3600;
const FACE_CHECK_INTERVAL = 1000;
const TAB_SWITCH_LIMIT = 3;
const FACE_VERIFY_INTERVAL = 30000;
const FACE_VERIFY_CLIP_DURATION = 5000;

const getTokenFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
};

const useTechnicalAssessment = () => {
  const [interviewDetail, setInterviewDetail] = useState(null);
  const [apiQuestions, setApiQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [savedQuestions, setSavedQuestions] = useState({});

  const [interviewStarted, setInterviewStarted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [globalTimeLeft, setGlobalTimeLeft] = useState(TEST_DURATION);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [interviewStatus, setInterviewStatus] = useState("loading");
  const [canStartInterview, setCanStartInterview] = useState(false);
  const [countdownText, setCountdownText] = useState("");

  const [openGuidelinesDialog, setOpenGuidelinesDialog] = useState(false);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);
  const [showFullScreenDialog, setShowFullScreenDialog] = useState(false);
  const [isInterviewSubmitted, setIsInterviewSubmitted] = useState(false);
  const [statusDialogTitle, setStatusDialogTitle] = useState("");
  const [statusDialogMessage, setStatusDialogMessage] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  const [faceMessage, setFaceMessage] = useState("");
  const [showFaceDialog, setShowFaceDialog] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [tabSwitchWarning, setTabSwitchWarning] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [otpErrorMsg, setOtpErrorMsg] = useState("");

  const localVideoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const facemeshModelRef = useRef(null);
  const faceIntervalRef = useRef(null);
  const storageKeyRef = useRef(null);
  const timerKeyRef = useRef(null);

  const validateMutation = useValidateProgrammingTest();
  const sendOtpMutation = useSendProgrammingTestOTP();
  const submitMutation = useSubmitProgrammingTest();
  const blockMutation = useBlockProgrammingTestById();

  const saveState = useCallback(() => {
    if (!storageKeyRef.current) return;
    try {
      const payload = {
        answers,
        currentQuestion,
        savedQuestions,
        interviewStarted,
        startTimestamp: Date.now(),
      };
      localStorage.setItem(storageKeyRef.current, JSON.stringify(payload));
      localStorage.setItem(
        timerKeyRef.current,
        JSON.stringify({ timeLeft: globalTimeLeft, savedAt: Date.now() }),
      );
    } catch (_) {}
  }, [
    answers,
    currentQuestion,
    savedQuestions,
    interviewStarted,
    globalTimeLeft,
  ]);

  const loadSavedState = useCallback(() => {
    if (!storageKeyRef.current) return null;
    try {
      const raw = localStorage.getItem(storageKeyRef.current);
      const timerRaw = localStorage.getItem(timerKeyRef.current);
      if (!raw || !timerRaw) return null;
      const state = JSON.parse(raw);
      const timer = JSON.parse(timerRaw);
      const elapsed = Math.floor((Date.now() - timer.savedAt) / 1000);
      const remaining = Math.max(0, timer.timeLeft - elapsed);
      return { ...state, timeLeft: remaining };
    } catch (_) {
      return null;
    }
  }, []);

  const clearSavedState = useCallback(() => {
    if (!storageKeyRef.current) return;
    localStorage.removeItem(storageKeyRef.current);
    localStorage.removeItem(timerKeyRef.current);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 320, height: 240 },
        audio: false,
      });
      streamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access denied:", err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startFaceDetection = useCallback(async () => {
    if (!facemeshModelRef.current) {
      facemeshModelRef.current = await facemesh.load({
        maxContinuousChecks: 5,
        detectionConfidence: 0.9,
        maxFaces: 2,
      });
    }

    if (faceIntervalRef.current) clearInterval(faceIntervalRef.current);

    faceIntervalRef.current = setInterval(async () => {
      const video = localVideoRef.current;
      if (!video || video.readyState < 2 || !facemeshModelRef.current) return;

      try {
        const predictions = await facemeshModelRef.current.estimateFaces(video);

        if (predictions.length === 0) {
          setFaceMessage(
            "Face Not Detected. Please ensure your face is visible to the camera.",
          );
          setShowFaceDialog(true);
        } else if (predictions.length > 1) {
          setFaceMessage(
            "Multiple faces detected. Please ensure only you are visible to the camera.",
          );
          setShowFaceDialog(true);
        } else {
          setShowFaceDialog(false);
          setFaceMessage("");
        }
      } catch (_) {}
    }, FACE_CHECK_INTERVAL);
  }, []);

  const stopFaceDetection = useCallback(() => {
    if (faceIntervalRef.current) {
      clearInterval(faceIntervalRef.current);
      faceIntervalRef.current = null;
    }
  }, []);

  const faceVerifyIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const isRecordingVerification = useRef(false);

  const sendFaceVerificationVideo = useCallback(
    async (videoBlob) => {
      if (!interviewDetail?._id || !videoBlob || videoBlob.size < 1000) return;

      try {
        const formData = new FormData();
        const videoFile = new File([videoBlob], "face-verify-clip.mp4", {
          type: "video/mp4",
        });
        formData.append("video", videoFile);

        if (interviewDetail?.profilePic) {
          try {
            const photoRes = await axios.get(interviewDetail.profilePic, {
              responseType: "blob",
            });
            const photoFile = new File([photoRes.data], "scheduled-photo.jpg", {
              type: photoRes.data.type,
              lastModified: Date.now(),
            });
            formData.append("scheduled_photo", photoFile);
          } catch (photoErr) {
            console.error("Error fetching profile photo:", photoErr);
          }
        }

        await axios.post(
          `${getPythonBaseUrl()}/coding-verify-face-video/?candidate_id=${
            interviewDetail._id
          }`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } catch (err) {
        console.error("Face verification API error:", err);
      }
    },
    [interviewDetail],
  );

  const recordAndVerifyFace = useCallback(() => {
    if (isRecordingVerification.current || !streamRef.current) return;

    const stream = streamRef.current;
    if (!stream.active || stream.getVideoTracks().length === 0) return;

    isRecordingVerification.current = true;

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
      ? "video/webm;codecs=vp8"
      : "video/webm";

    const chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      isRecordingVerification.current = false;
      mediaRecorderRef.current = null;
      if (chunks.length > 0) {
        const blob = new Blob(chunks, { type: mimeType });
        sendFaceVerificationVideo(blob);
      }
    };

    recorder.onerror = () => {
      isRecordingVerification.current = false;
      mediaRecorderRef.current = null;
    };

    recorder.start();
    setTimeout(() => {
      if (recorder.state === "recording") {
        recorder.stop();
      }
    }, FACE_VERIFY_CLIP_DURATION);
  }, [sendFaceVerificationVideo]);

  const startFaceVerification = useCallback(() => {
    if (faceVerifyIntervalRef.current)
      clearInterval(faceVerifyIntervalRef.current);

    setTimeout(() => recordAndVerifyFace(), 3000);

    faceVerifyIntervalRef.current = setInterval(() => {
      recordAndVerifyFace();
    }, FACE_VERIFY_INTERVAL);
  }, [recordAndVerifyFace]);

  const stopFaceVerification = useCallback(() => {
    if (faceVerifyIntervalRef.current) {
      clearInterval(faceVerifyIntervalRef.current);
      faceVerifyIntervalRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    isRecordingVerification.current = false;
  }, []);

  const startTimer = useCallback((initialTime = TEST_DURATION) => {
    setGlobalTimeLeft(initialTime);

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setGlobalTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const formatTime = useCallback((seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, []);

  const validateToken = useCallback(
    (otpCode) => {
      const token = getTokenFromUrl();
      if (!token) {
        setInterviewStatus("invalid");
        setIsInterviewSubmitted(true);
        setStatusDialogTitle("Invalid Link");
        setStatusDialogMessage("No valid token found in the URL.");
        return;
      }

      setOtpErrorMsg("");
      validateMutation.mutate(
        { token, ...(otpCode ? { otp: otpCode } : {}) },
        {
          onSuccess: (data) => {
            if (data.otpRequired) {
              setMaskedEmail(data.maskedEmail || "");
              setCandidateName(data.candidateName || "");
              return;
            }

            const detail = data.interviewDetail;
            if (!detail) {
              setInterviewStatus("invalid");
              setIsInterviewSubmitted(true);
              setStatusDialogTitle("Invalid Link");
              setStatusDialogMessage("Unable to validate the assessment link.");
              return;
            }

            setIsOtpVerified(true);
            setInterviewDetail(detail);
            setApiQuestions(detail.coding_questions?.questions || []);

            storageKeyRef.current = STORAGE_KEY_PREFIX + detail._id;
            timerKeyRef.current = TIMER_KEY_PREFIX + detail._id;

            const initAnswers = {};
            (detail.coding_questions?.questions || []).forEach((q) => {
              initAnswers[q.id] = q.code_snippet || "";
            });

            const saved = loadSavedState();
            if (saved && saved.interviewStarted && saved.timeLeft > 0) {
              setAnswers({ ...initAnswers, ...saved.answers });
              setCurrentQuestion(saved.currentQuestion || 0);
              setSavedQuestions(saved.savedQuestions || {});
              setInterviewStatus("ready");
              setCanStartInterview(true);
            } else {
              setAnswers(initAnswers);
              setInterviewStatus("ready");
            }
          },
          onError: (error) => {
            const msg =
              error?.response?.data?.message ||
              "Unable to validate the assessment link.";
            if (otpCode) {
              setOtpErrorMsg(msg);
            } else {
              setInterviewStatus("ended");
              setIsInterviewSubmitted(true);
              setStatusDialogTitle("Validation Error");
              setStatusDialogMessage(msg);
            }
          },
        },
      );
    },
    [validateMutation, loadSavedState],
  );

  const handleSendOTP = useCallback(async () => {
    const token = getTokenFromUrl();
    if (!token) return false;
    try {
      setOtpErrorMsg("");
      const res = await sendOtpMutation.mutateAsync({ token });
      if (res?.maskedEmail) {
        setMaskedEmail(res.maskedEmail);
      }
      return true;
    } catch (err) {
      setOtpErrorMsg(
        err.response?.data?.message || "Failed to send OTP. Please try again.",
      );
      return false;
    }
  }, [sendOtpMutation]);

  const handleVerifyOTP = useCallback(
    (otpCode) => {
      validateToken(otpCode);
    },
    [validateToken],
  );

  useEffect(() => {
    validateToken();
    return () => {
      stopTimer();
      stopFaceVerification();
    };
  }, []);

  useEffect(() => {
    if (!interviewDetail) return;

    const checkWindow = () => {
      const now = moment.utc();
      const start = moment.utc(interviewDetail.startTime);
      const end = moment.utc(interviewDetail.endTime);

      if (
        interviewDetail.status === "Cancelled" ||
        interviewDetail.status === "Blocked"
      ) {
        setInterviewStatus("ended");
        setIsInterviewSubmitted(true);
        setStatusDialogTitle("Assessment Cancelled");
        setStatusDialogMessage("This assessment has been cancelled.");
        return;
      }

      if (now.isBefore(start)) {
        const diff = moment.duration(start.diff(now));
        const h = Math.floor(diff.asHours());
        const m = diff.minutes();
        const s = diff.seconds();
        setCountdownText(
          `Assessment starts in ${h > 0 ? h + "h " : ""}${m}m ${s}s`,
        );
        setCanStartInterview(false);
        setInterviewStatus("waiting");
      } else if (now.isBetween(start, end)) {
        setCanStartInterview(true);
        setCountdownText("");
        setInterviewStatus("ready");
      } else {
        setInterviewStatus("ended");
        setIsInterviewSubmitted(true);
        setStatusDialogTitle("Assessment Window Closed");
        setStatusDialogMessage(
          "The scheduled time window for this assessment has ended.",
        );
      }
    };

    checkWindow();
    const interval = setInterval(checkWindow, 1000);
    return () => clearInterval(interval);
  }, [interviewDetail]);

  useEffect(() => {
    if (globalTimeLeft === 0 && interviewStarted && !isSubmitted) {
      handleSubmit();
    }
  }, [globalTimeLeft, interviewStarted, isSubmitted]);

  useEffect(() => {
    if (!interviewStarted) return;
    const interval = setInterval(saveState, 5000);
    return () => clearInterval(interval);
  }, [interviewStarted, saveState]);

  useEffect(() => {
    if (redirectCountdown === null || !interviewDetail?.aiInterviewLink) return;

    if (redirectCountdown <= 0) {
      window.location.href = interviewDetail.aiInterviewLink;
      return;
    }

    setStatusDialogMessage(
      `Great job! Redirecting you to the AI Interview in ${redirectCountdown} second${
        redirectCountdown > 1 ? "s" : ""
      }...`,
    );

    const timer = setTimeout(() => {
      setRedirectCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [redirectCountdown, interviewDetail]);

  useEffect(() => {
    if (!interviewStarted || isSubmitted) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount((prev) => {
          const next = prev + 1;
          if (next >= TAB_SWITCH_LIMIT) {
            blockMutation.mutate(interviewDetail?._id);
            setIsInterviewSubmitted(true);
            setStatusDialogTitle("Assessment Cancelled");
            setStatusDialogMessage(
              "Your assessment has been cancelled due to multiple tab switches.",
            );
            stopTimer();
            stopFaceDetection();
            stopFaceVerification();
            stopCamera();
            clearSavedState();
          } else {
            const remaining = TAB_SWITCH_LIMIT - next;
            setTabSwitchWarning(
              `Warning! Switching tabs is not allowed. You have ${remaining} attempt${
                remaining > 1 ? "s" : ""
              } remaining before your assessment is cancelled.`,
            );
          }
          return next;
        });
      }
    };

    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        ["c", "v", "x", "s", "a"].includes(e.key.toLowerCase())
      ) {
        e.preventDefault();
      }
      if (e.key === "PrintScreen") {
        e.preventDefault();
      }
    };

    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [
    interviewStarted,
    isSubmitted,
    interviewDetail,
    blockMutation,
    stopTimer,
    stopFaceDetection,
    stopFaceVerification,
    stopCamera,
    clearSavedState,
  ]);

  useEffect(() => {
    if (!interviewStarted || isSubmitted) return;

    const handleFullScreenChange = () => {
      if (!document.fullscreenElement) {
        setShowFullScreenDialog(true);
      } else {
        setShowFullScreenDialog(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
  }, [interviewStarted, isSubmitted]);

  const handleStartTest = useCallback(async () => {
    setInterviewStarted(true);
    await startCamera();
    startFaceDetection();
    startFaceVerification();

    const saved = loadSavedState();
    if (saved && saved.interviewStarted && saved.timeLeft > 0) {
      startTimer(saved.timeLeft);
    } else {
      startTimer(TEST_DURATION);
    }

    try {
      await document.documentElement.requestFullscreen();
    } catch (_) {}
  }, [startTimer, loadSavedState, startFaceVerification]);

  const handleEnterFullScreen = useCallback(async () => {
    try {
      await document.documentElement.requestFullscreen();
      setShowFullScreenDialog(false);
    } catch (_) {}
  }, []);

  const handleCodeChange = useCallback(
    (value) => {
      if (!apiQuestions[currentQuestion]) return;
      const qId = apiQuestions[currentQuestion].id;
      setAnswers((prev) => ({ ...prev, [qId]: value || "" }));
    },
    [currentQuestion, apiQuestions],
  );

  const handleSave = useCallback(() => {
    setSavedQuestions((prev) => ({ ...prev, [currentQuestion]: true }));
    saveState();
  }, [currentQuestion, saveState]);

  const handleNext = useCallback(() => {
    if (currentQuestion < apiQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  }, [currentQuestion, apiQuestions.length]);

  const handlePrevious = useCallback(() => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  }, [currentQuestion]);

  const handleQuestionClick = useCallback((index) => {
    setCurrentQuestion(index);
  }, []);

  const handleConfirmSubmit = useCallback(() => {
    setOpenSubmitDialog(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isSubmitted) return;
    setIsSubmitting(true);
    setOpenSubmitDialog(false);

    const payload = {
      candidate_id: interviewDetail?._id,
      recruiter_email: interviewDetail?.recruiterEmail,
      question_and_answer: apiQuestions.map((q) => ({
        title: q.title,
        code: answers[q.id] || q.code_snippet || "",
      })),
    };

    try {
      await submitMutation.mutateAsync(payload);

      setIsSubmitted(true);
      setIsInterviewSubmitted(true);

      const hasAiInterview = !!interviewDetail?.aiInterviewLink;

      if (hasAiInterview) {
        setStatusDialogTitle("Assessment Completed");
        setStatusDialogMessage(
          "Great job! Redirecting you to the AI Interview in a few seconds...",
        );
        setRedirectCountdown(5);
      } else {
        setStatusDialogTitle("Assessment Completed");
        setStatusDialogMessage(
          "Thank you for completing the assessment. Your responses have been submitted successfully.",
        );
      }

      stopTimer();
      stopFaceDetection();
      stopFaceVerification();
      stopCamera();
      clearSavedState();

      try {
        if (document.fullscreenElement) await document.exitFullscreen();
      } catch (_) {}
    } catch (err) {
      console.error("Submit error:", err);
      setIsSubmitting(false);
      setIsInterviewSubmitted(true);
      setStatusDialogTitle("Submission Error");
      setStatusDialogMessage(
        "An error occurred while submitting. Please try again.",
      );
    }
  }, [
    isSubmitting,
    isSubmitted,
    interviewDetail,
    apiQuestions,
    answers,
    submitMutation,
    stopTimer,
    stopFaceDetection,
    stopFaceVerification,
    stopCamera,
    clearSavedState,
  ]);

  const currentQ = apiQuestions[currentQuestion] || null;
  const currentCode =
    currentQ && answers[currentQ.id] !== undefined
      ? answers[currentQ.id]
      : currentQ?.code_snippet || "";

  return {
    interviewDetail,
    apiQuestions,
    currentQuestion,
    currentQ,
    currentCode,
    savedQuestions,
    interviewStarted,
    agreed,
    setAgreed,
    globalTimeLeft,
    formatTime,
    isSubmitting,
    interviewStatus,
    canStartInterview,
    countdownText,
    openGuidelinesDialog,
    setOpenGuidelinesDialog,
    openSubmitDialog,
    setOpenSubmitDialog,
    showFullScreenDialog,
    isInterviewSubmitted,
    statusDialogTitle,
    statusDialogMessage,
    faceMessage,
    showFaceDialog,
    localVideoRef,
    handleStartTest,
    handleEnterFullScreen,
    handleCodeChange,
    handleSave,
    handleNext,
    handlePrevious,
    handleQuestionClick,
    handleConfirmSubmit,
    handleSubmit,
    tabSwitchWarning,
    setTabSwitchWarning,
    isOtpVerified,
    maskedEmail,
    candidateName,
    otpErrorMsg,
    handleSendOTP,
    handleVerifyOTP,
    isValidationPending: validateMutation.isPending || sendOtpMutation.isPending,
    isSendOtpPending: sendOtpMutation.isPending,
    isValidatePending: validateMutation.isPending,
  };
};

export default useTechnicalAssessment;
